import React, { useState, useEffect } from 'react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { getLatestRecord, createRecord } from '../services/api';

function SectionForm({ section, patientId, recordedBy }) {
  const [odData, setOdData] = useState({});
  const [osData, setOsData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLatestData();
  }, [section, patientId]);

  const loadLatestData = async () => {
    setLoading(true);
    try {
      const [odRecord, osRecord] = await Promise.all([
        getLatestRecord(section, patientId, 'od'),
        getLatestRecord(section, patientId, 'os'),
      ]);

      if (odRecord) {
        setOdData(odRecord.data || {});
      } else {
        setOdData({});
      }

      if (osRecord) {
        setOsData(osRecord.data || {});
      } else {
        setOsData({});
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (eye) => {
    const data = eye === 'OD' ? odData : osData;
    
    try {
      await createRecord(section, patientId, eye.toLowerCase(), data, recordedBy);
      alert(`${section.toUpperCase()} saved for ${eye}`);
      // Refresh the page while maintaining the current section
      window.location.hash = section;
      window.location.reload();
    } catch (error) {
      alert('Error saving record: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleClear = (eye) => {
    if (eye === 'OD') {
      setOdData({});
    } else {
      setOsData({});
    }
  };

  const updateField = (eye, field, value) => {
    if (eye === 'OD') {
      setOdData({ ...odData, [field]: value || null });
    } else {
      setOsData({ ...osData, [field]: value || null });
    }
  };

  if (loading) {
    return <div className="text-center py-4">Loading...</div>;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
      <EyePanel
        eye="OD"
        data={odData}
        section={section}
        onUpdate={(field, value) => updateField('OD', field, value)}
        onSave={() => handleSave('OD')}
        onClear={() => handleClear('OD')}
      />
      <EyePanel
        eye="OS"
        data={osData}
        section={section}
        onUpdate={(field, value) => updateField('OS', field, value)}
        onSave={() => handleSave('OS')}
        onClear={() => handleClear('OS')}
      />
    </div>
  );
}

function EyePanel({ eye, data, section, onUpdate, onSave, onClear }) {
  const renderFields = () => {
    switch (section) {
      case 'acuity':
        return (
          <>
            <FormRow label="Unaided">
              <Input
                value={data.unaided || ''}
                onChange={(e) => onUpdate('unaided', e.target.value)}
                placeholder="e.g. 6/6"
              />
            </FormRow>
            <FormRow label="Pinhole">
              <Input
                value={data.pinhole || ''}
                onChange={(e) => onUpdate('pinhole', e.target.value)}
              />
            </FormRow>
            <FormRow label="BCVA">
              <Input
                value={data.bcva || ''}
                onChange={(e) => onUpdate('bcva', e.target.value)}
              />
            </FormRow>
            <FormRow label="Near">
              <Input
                value={data.near || ''}
                onChange={(e) => onUpdate('near', e.target.value)}
              />
            </FormRow>
            <FormRow label="Method">
              <Select
                value={data.method || ''}
                onValueChange={(value) => onUpdate('method', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Snellen">Snellen</SelectItem>
                  <SelectItem value="ETDRS">ETDRS</SelectItem>
                  <SelectItem value="LogMAR">LogMAR</SelectItem>
                  <SelectItem value="Jaeger">Jaeger</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </FormRow>
            <FormRow label="Notes">
              <Textarea
                value={data.notes || ''}
                onChange={(e) => onUpdate('notes', e.target.value)}
                rows={2}
                placeholder="VA notes"
              />
            </FormRow>
          </>
        );

      case 'refraction':
        return (
          <>
            <FormRow label="Obj Sph (D)">
              <Input
                type="number"
                step="0.25"
                min="-30"
                max="30"
                value={data.obj_sph || ''}
                onChange={(e) => onUpdate('obj_sph', e.target.value ? parseFloat(e.target.value) : null)}
              />
            </FormRow>
            <FormRow label="Obj Cyl (D)">
              <Input
                type="number"
                step="0.25"
                min="-15"
                max="15"
                value={data.obj_cyl || ''}
                onChange={(e) => onUpdate('obj_cyl', e.target.value ? parseFloat(e.target.value) : null)}
              />
            </FormRow>
            <FormRow label="Obj Axis (°)">
              <Input
                type="number"
                step="1"
                min="0"
                max="180"
                value={data.obj_axis || ''}
                onChange={(e) => onUpdate('obj_axis', e.target.value ? parseInt(e.target.value) : null)}
              />
            </FormRow>
            <FormRow label="Sub Sph (D)">
              <Input
                type="number"
                step="0.25"
                min="-30"
                max="30"
                value={data.sub_sph || ''}
                onChange={(e) => onUpdate('sub_sph', e.target.value ? parseFloat(e.target.value) : null)}
              />
            </FormRow>
            <FormRow label="Sub Cyl (D)">
              <Input
                type="number"
                step="0.25"
                min="-15"
                max="15"
                value={data.sub_cyl || ''}
                onChange={(e) => onUpdate('sub_cyl', e.target.value ? parseFloat(e.target.value) : null)}
              />
            </FormRow>
            <FormRow label="Sub Axis (°)">
              <Input
                type="number"
                step="1"
                min="0"
                max="180"
                value={data.sub_axis || ''}
                onChange={(e) => onUpdate('sub_axis', e.target.value ? parseInt(e.target.value) : null)}
              />
            </FormRow>
            <FormRow label="Add (D)">
              <Input
                type="number"
                step="0.25"
                min="0.5"
                max="4.0"
                value={data.add || ''}
                onChange={(e) => onUpdate('add', e.target.value ? parseFloat(e.target.value) : null)}
              />
            </FormRow>
            <FormRow label="PD (mm)">
              <Input
                type="number"
                step="0.5"
                min="40"
                max="80"
                value={data.pd || ''}
                onChange={(e) => onUpdate('pd', e.target.value ? parseFloat(e.target.value) : null)}
              />
            </FormRow>
            <FormRow label="Notes">
              <Textarea
                value={data.notes || ''}
                onChange={(e) => onUpdate('notes', e.target.value)}
                rows={2}
                placeholder="Refraction notes"
              />
            </FormRow>
          </>
        );

      case 'iop':
        return (
          <>
            <FormRow label="IOP (mmHg)">
              <Input
                type="number"
                step="0.1"
                min="0"
                max="80"
                value={data.value || ''}
                onChange={(e) => onUpdate('value', e.target.value ? parseFloat(e.target.value) : null)}
              />
            </FormRow>
            <FormRow label="Method">
              <Select
                value={data.method || ''}
                onValueChange={(value) => onUpdate('method', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Goldmann">Goldmann</SelectItem>
                  <SelectItem value="NCT">NCT</SelectItem>
                  <SelectItem value="Tonopen">Tonopen</SelectItem>
                  <SelectItem value="iCare">iCare</SelectItem>
                  <SelectItem value="Perkins">Perkins</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </FormRow>
            <FormRow label="Notes">
              <Textarea
                value={data.notes || ''}
                onChange={(e) => onUpdate('notes', e.target.value)}
                rows={2}
                placeholder="IOP notes"
              />
            </FormRow>
          </>
        );

      case 'slitlamp':
        return (
          <>
            <FormRow label="Lids">
              <Select
                value={data.lids || ''}
                onValueChange={(value) => onUpdate('lids', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Normal">Normal</SelectItem>
                  <SelectItem value="Ptosis">Ptosis</SelectItem>
                  <SelectItem value="Swelling">Swelling</SelectItem>
                  <SelectItem value="Lesion">Lesion</SelectItem>
                  <SelectItem value="Entropion">Entropion</SelectItem>
                  <SelectItem value="Ectropion">Ectropion</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </FormRow>
            <FormRow label="Conjunctiva">
              <Select
                value={data.conjunctiva || ''}
                onValueChange={(value) => onUpdate('conjunctiva', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Normal">Normal</SelectItem>
                  <SelectItem value="Hyperemia">Hyperemia</SelectItem>
                  <SelectItem value="Chemosis">Chemosis</SelectItem>
                  <SelectItem value="Papillae">Papillae</SelectItem>
                  <SelectItem value="Follicles">Follicles</SelectItem>
                  <SelectItem value="Pinguecula">Pinguecula</SelectItem>
                  <SelectItem value="Pterygium">Pterygium</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </FormRow>
            <FormRow label="Cornea">
              <Input
                value={data.cornea || ''}
                onChange={(e) => onUpdate('cornea', e.target.value)}
                placeholder="cornea findings"
              />
            </FormRow>
            <FormRow label="AC">
              <Input
                value={data.ac || ''}
                onChange={(e) => onUpdate('ac', e.target.value)}
                placeholder="anterior chamber"
              />
            </FormRow>
            <FormRow label="Iris">
              <Input
                value={data.iris || ''}
                onChange={(e) => onUpdate('iris', e.target.value)}
                placeholder="iris findings"
              />
            </FormRow>
            <FormRow label="Lens">
              <Select
                value={data.lens || ''}
                onValueChange={(value) => onUpdate('lens', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Clear">Clear</SelectItem>
                  <SelectItem value="NS1">NS1</SelectItem>
                  <SelectItem value="NS2">NS2</SelectItem>
                  <SelectItem value="NS3">NS3</SelectItem>
                  <SelectItem value="NS4">NS4</SelectItem>
                  <SelectItem value="Cortical">Cortical</SelectItem>
                  <SelectItem value="PSC">PSC</SelectItem>
                  <SelectItem value="Aphakia">Aphakia</SelectItem>
                  <SelectItem value="Pseudophakia">Pseudophakia</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </FormRow>
            <FormRow label="Notes">
              <Textarea
                value={data.notes || ''}
                onChange={(e) => onUpdate('notes', e.target.value)}
                rows={2}
                placeholder="Slit lamp notes"
              />
            </FormRow>
          </>
        );

      case 'fundus':
        return (
          <>
            <FormRow label="Optic Disc">
              <Select
                value={data.optic_disc || ''}
                onValueChange={(value) => onUpdate('optic_disc', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Normal">Normal</SelectItem>
                  <SelectItem value="Pallor">Pallor</SelectItem>
                  <SelectItem value="Edema">Edema</SelectItem>
                  <SelectItem value="Cupping">Cupping</SelectItem>
                  <SelectItem value="Drusen">Drusen</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </FormRow>
            <FormRow label="Macula">
              <Select
                value={data.macula || ''}
                onValueChange={(value) => onUpdate('macula', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Normal">Normal</SelectItem>
                  <SelectItem value="Edema">Edema</SelectItem>
                  <SelectItem value="Scar">Scar</SelectItem>
                  <SelectItem value="Drusen">Drusen</SelectItem>
                  <SelectItem value="Hemorrhage">Hemorrhage</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </FormRow>
            <FormRow label="Vessels">
              <Select
                value={data.vessels || ''}
                onValueChange={(value) => onUpdate('vessels', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Normal">Normal</SelectItem>
                  <SelectItem value="Attenuated">Attenuated</SelectItem>
                  <SelectItem value="Dilated">Dilated</SelectItem>
                  <SelectItem value="Tortuous">Tortuous</SelectItem>
                  <SelectItem value="AV Nicking">AV Nicking</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </FormRow>
            <FormRow label="Cup/Disc">
              <Input
                type="number"
                step="0.01"
                min="0"
                max="1"
                value={data.cdr || ''}
                onChange={(e) => onUpdate('cdr', e.target.value ? parseFloat(e.target.value) : null)}
              />
            </FormRow>
            <FormRow label="Notes">
              <Textarea
                value={data.notes || ''}
                onChange={(e) => onUpdate('notes', e.target.value)}
                rows={2}
                placeholder="Fundus / OCT summary"
              />
            </FormRow>
          </>
        );

      case 'vf':
        return (
          <>
            <FormRow label="Test Type">
              <Select
                value={data.test_type || ''}
                onValueChange={(value) => onUpdate('test_type', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="24-2">24-2</SelectItem>
                  <SelectItem value="30-2">30-2</SelectItem>
                  <SelectItem value="10-2">10-2</SelectItem>
                  <SelectItem value="FastPac">FastPac</SelectItem>
                  <SelectItem value="SITA-Fast">SITA-Fast</SelectItem>
                  <SelectItem value="SITA-Standard">SITA-Standard</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </FormRow>
            <FormRow label="MD (dB)">
              <Input
                type="number"
                step="0.1"
                min="-40"
                max="5"
                value={data.md || ''}
                onChange={(e) => onUpdate('md', e.target.value ? parseFloat(e.target.value) : null)}
              />
            </FormRow>
            <FormRow label="PSD (dB)">
              <Input
                type="number"
                step="0.1"
                min="0"
                max="20"
                value={data.psd || ''}
                onChange={(e) => onUpdate('psd', e.target.value ? parseFloat(e.target.value) : null)}
              />
            </FormRow>
            <FormRow label="VFI (%)">
              <Input
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={data.vfi || ''}
                onChange={(e) => onUpdate('vfi', e.target.value ? parseFloat(e.target.value) : null)}
              />
            </FormRow>
            <FormRow label="Notes">
              <Textarea
                value={data.notes || ''}
                onChange={(e) => onUpdate('notes', e.target.value)}
                rows={2}
                placeholder="Field reliability notes"
              />
            </FormRow>
          </>
        );

      case 'keratometry':
        return (
          <>
            <FormRow label="k1 (D)">
              <Input
                type="number"
                step="0.01"
                min="30"
                max="60"
                value={data.k1 || ''}
                onChange={(e) => onUpdate('k1', e.target.value ? parseFloat(e.target.value) : null)}
              />
            </FormRow>
            <FormRow label="k2 (D)">
              <Input
                type="number"
                step="0.01"
                min="30"
                max="60"
                value={data.k2 || ''}
                onChange={(e) => onUpdate('k2', e.target.value ? parseFloat(e.target.value) : null)}
              />
            </FormRow>
            <FormRow label="k axis (°)">
              <Input
                type="number"
                step="1"
                min="0"
                max="180"
                value={data.k_axis || ''}
                onChange={(e) => onUpdate('k_axis', e.target.value ? parseInt(e.target.value) : null)}
              />
            </FormRow>
            <FormRow label="Pachymetry (µm)">
              <Input
                type="number"
                step="1"
                min="300"
                max="700"
                value={data.pachymetry || ''}
                onChange={(e) => onUpdate('pachymetry', e.target.value ? parseInt(e.target.value) : null)}
              />
            </FormRow>
            <FormRow label="Axial Length (mm)">
              <Input
                type="number"
                step="0.01"
                min="18"
                max="32"
                value={data.axial_length || ''}
                onChange={(e) => onUpdate('axial_length', e.target.value ? parseFloat(e.target.value) : null)}
              />
            </FormRow>
            <FormRow label="ACD (mm)">
              <Input
                type="number"
                step="0.01"
                min="2.0"
                max="5.0"
                value={data.acd || ''}
                onChange={(e) => onUpdate('acd', e.target.value ? parseFloat(e.target.value) : null)}
              />
            </FormRow>
            <FormRow label="Lens Thickness (mm)">
              <Input
                type="number"
                step="0.01"
                min="2.0"
                max="6.0"
                value={data.lens_thickness || ''}
                onChange={(e) => onUpdate('lens_thickness', e.target.value ? parseFloat(e.target.value) : null)}
              />
            </FormRow>
            <FormRow label="IOL Power (D)">
              <Input
                type="number"
                step="0.25"
                min="-10"
                max="40"
                value={data.iol_power || ''}
                onChange={(e) => onUpdate('iol_power', e.target.value ? parseFloat(e.target.value) : null)}
              />
            </FormRow>
            <FormRow label="Notes">
              <Textarea
                value={data.notes || ''}
                onChange={(e) => onUpdate('notes', e.target.value)}
                rows={2}
                placeholder="Topo / pachy / biometry notes"
              />
            </FormRow>
          </>
        );

      case 'gonioscopy':
        return (
          <>
            <FormRow label="Superior (0-4)">
              <Input
                type="number"
                step="1"
                min="0"
                max="4"
                value={data.sup || ''}
                onChange={(e) => onUpdate('sup', e.target.value ? parseInt(e.target.value) : null)}
              />
            </FormRow>
            <FormRow label="Inferior (0-4)">
              <Input
                type="number"
                step="1"
                min="0"
                max="4"
                value={data.inf || ''}
                onChange={(e) => onUpdate('inf', e.target.value ? parseInt(e.target.value) : null)}
              />
            </FormRow>
            <FormRow label="Nasal (0-4)">
              <Input
                type="number"
                step="1"
                min="0"
                max="4"
                value={data.nasal || ''}
                onChange={(e) => onUpdate('nasal', e.target.value ? parseInt(e.target.value) : null)}
              />
            </FormRow>
            <FormRow label="Temporal (0-4)">
              <Input
                type="number"
                step="1"
                min="0"
                max="4"
                value={data.temporal || ''}
                onChange={(e) => onUpdate('temporal', e.target.value ? parseInt(e.target.value) : null)}
              />
            </FormRow>
            <FormRow label="Overall">
              <Select
                value={data.overall || ''}
                onValueChange={(value) => onUpdate('overall', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Open">Open</SelectItem>
                  <SelectItem value="Narrow">Narrow</SelectItem>
                  <SelectItem value="Closed">Closed</SelectItem>
                  <SelectItem value="PAS">PAS</SelectItem>
                </SelectContent>
              </Select>
            </FormRow>
            <FormRow label="Notes">
              <Textarea
                value={data.notes || ''}
                onChange={(e) => onUpdate('notes', e.target.value)}
                rows={2}
                placeholder="Gonio notes"
              />
            </FormRow>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white">
      <h4 className="text-lg font-semibold mb-4 text-[#2c5aa0]">
        {eye} — {section.charAt(0).toUpperCase() + section.slice(1)}
      </h4>
      <div className="space-y-3">{renderFields()}</div>
      <div className="flex justify-between gap-2 mt-4">
        <Button onClick={onSave} variant="default">
          Save {eye}
        </Button>
        <Button onClick={onClear} variant="ghost">
          Clear
        </Button>
      </div>
    </div>
  );
}

function FormRow({ label, children }) {
  return (
    <div className="flex items-center gap-3">
      <Label className="min-w-[130px] font-semibold text-gray-700">{label}</Label>
      <div className="flex-1">{children}</div>
    </div>
  );
}

export default SectionForm;

